<template>
  <v-container fluid class="pa-4">
    <!-- Pantalla de carga -->
    <div v-if="loading && !listLoaded" class="center-container">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4 text-h6">Cargando lista...</p>
      </div>
    </div>

    <!-- Pantalla de error si no se puede cargar -->
    <div v-else-if="!listLoaded && !loading" class="center-container">
      <v-card class="pa-6 text-center" max-width="600">
        <v-icon size="100" color="error" class="mb-4">mdi-alert-circle</v-icon>
        <h2 class="mb-4">Esta lista es privada</h2>
        <p>Necesitas la contraseña para acceder a ella.</p>
        
        <!-- Si requiere contraseña -->
        <v-alert v-if="requiresPassword && !isAdminMode" class="mb-4">          
          <v-text-field
            v-model="password"
            label="Contraseña de la lista"
            prepend-inner-icon="mdi-lock"
            variant="outlined"
            type="password"
            class="mt-4"
            @keyup.enter="loadList"
          ></v-text-field>
          
          <v-btn
            color="warning"
            block
            @click="loadList"
            :loading="loading"
            class="mt-2"
          >
            <v-icon start>mdi-lock-open</v-icon>
            Acceder con contraseña
          </v-btn>
        </v-alert>

        <!-- Si es modo administrador -->
        <v-alert v-else-if="isAdminMode" type="info" variant="tonal" class="mb-4">
          <h3 class="mb-2">Acceso de administrador</h3>
          <p>Introduce tu email para gestionar esta lista.</p>
          
          <v-text-field
            v-model="email"
            label="Email del administrador"
            prepend-inner-icon="mdi-email"
            variant="outlined"
            type="email"
            class="mt-4"
            @keyup.enter="loadList"
          ></v-text-field>
          
          <v-btn
            color="primary"
            block
            @click="loadList"
            :loading="loading"
            class="mt-2"
          >
            <v-icon start>mdi-shield-crown</v-icon>
            Acceder como administrador
          </v-btn>
        </v-alert>

        <!-- Mensaje de error general -->
        <v-alert v-else type="error" variant="tonal" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <v-divider class="my-4"></v-divider>

        <div class="d-flex flex-column gap-2">
          <v-btn
            color="primary"
            variant="outlined"
            @click="$router.push('/')"
          >
            <v-icon start>mdi-arrow-left</v-icon>
            Volver al inicio
          </v-btn>

          <v-btn
            v-if="!isAdminMode"
            color="secondary"
            variant="text"
            @click="toggleAdminMode"
          >
            <v-icon start>mdi-shield-account</v-icon>
            Soy el administrador de esta lista
          </v-btn>

          <v-btn
            v-else
            color="grey"
            variant="text"
            @click="toggleAdminMode"
          >
            <v-icon start>mdi-arrow-left</v-icon>
            Volver a modo visitante
          </v-btn>
        </div>
      </v-card>
    </div>

    <!-- Pantalla de lista cargada -->
    <div v-else>
      <!-- Header de la lista -->
      <v-card class="mb-4" elevation="2">
        <v-card-title class="pa-4 pa-md-6">
          <!-- Icono y título centrados -->
          <div class="d-flex flex-column align-center text-center w-100 mb-4">
            <v-icon :icon="listData.icon" size="80" :color="listData.color" class="mb-3"></v-icon>
            <h1 class="text-h5 text-md-h4 mb-2">{{ listData.name }}</h1>
            <p class="text-subtitle-2 text-md-subtitle-1 text-medium-emphasis mb-0">
              {{ listData.type }}
            </p>
          </div>
          
          <!-- Chips y botón de compartir -->
          <div class="d-flex flex-wrap gap-2 justify-center w-100">
            <v-chip v-if="isAdmin" color="primary" size="small">
              <v-icon start size="small">mdi-shield-crown</v-icon>
              Admin
            </v-chip>
            <v-chip v-if="listData.isPrivate" color="warning" size="small">
              <v-icon start size="small">mdi-lock</v-icon>
              Privada
            </v-chip>
          </div>
          <div class="d-flex flex-wrap gap-2 justify-center w-100">
            <v-btn
              icon="mdi-share-variant"
              variant="text"
              size="small"
              @click="shareList"
            ></v-btn>
          </div>
        </v-card-title>
      </v-card>

      <!-- Estadísticas -->
      <v-row class="mb-4">
        <v-col cols="12" sm="4">
          <v-card>
            <v-card-text class="text-center pa-4">
              <v-icon size="40" color="primary">mdi-gift-outline</v-icon>
              <div class="text-h5 mt-2">{{ listData.gifts.length }}</div>
              <div class="text-caption">Regalos totales</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card>
            <v-card-text class="text-center pa-4">
              <v-icon size="40" color="success">mdi-check-circle</v-icon>
              <div class="text-h5 mt-2">{{ reservedCount }}</div>
              <div class="text-caption">Reservados</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card>
            <v-card-text class="text-center pa-4">
              <v-icon size="40" color="grey">mdi-cart-outline</v-icon>
              <div class="text-h5 mt-2">{{ availableCount }}</div>
              <div class="text-caption">Disponibles</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Botones de acción para administrador -->
      <v-card v-if="isAdmin" class="mb-4 pa-3 pa-md-4" color="blue-lighten-5">
        <div class="admin-actions">
          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            @click="showAddGiftDialog = true"
            size="small"
            class="mb-2 mb-md-0"
          >
            Añadir regalo
          </v-btn>
          <v-btn
            color="secondary"
            prepend-icon="mdi-pencil"
            @click="openEditDialog"
            size="small"
            class="mb-2 mb-md-0"
          >
            Editar lista
          </v-btn>
          <v-btn
            color="error"
            variant="outlined"
            prepend-icon="mdi-delete"
            @click="showDeleteDialog = true"
            size="small"
          >
            Eliminar lista
          </v-btn>
        </div>
      </v-card>

      <!-- Grid de regalos -->
      <v-row>
        <v-col
          v-for="gift in listData.gifts"
          :key="gift.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card class="gift-card" height="100%">
            <div class="gift-icon-container">
              <!-- Si hay imagen del producto -->
              <v-img
                v-if="gift.productInfo?.image"
                :src="gift.productInfo.image"
                height="180"
                cover
                @error="onImageError(gift)"
              >
                <template v-slot:placeholder>
                  <div class="d-flex align-center justify-center fill-height">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>
                </template>
              </v-img>
              
              <!-- Si no hay imagen, mostrar logo o icono -->
              <div v-else class="default-gift-image">
                <!-- Intentar mostrar logo -->
                <v-img
                  v-if="logoUrl"
                  :src="logoUrl"
                  height="120"
                  contain
                  class="gift-logo"
                  @error="logoUrl = null"
                ></v-img>
                
                <!-- Si no hay logo, mostrar icono -->
                <v-icon
                  v-else
                  :size="isAdmin ? 60 : 80"
                  :color="gift.reserved ? 'grey' : 'primary'"
                >
                  {{ gift.reserved ? 'mdi-gift-off' : 'mdi-gift-outline' }}
                </v-icon>
              </div>
              
              <!-- Reserved badge -->
              <v-chip
                v-if="gift.reserved"
                class="reserved-badge"
                color="success"
                size="small"
              >
                <v-icon start size="small">mdi-check</v-icon>
                Reservado
              </v-chip>
            </div>

            <v-card-title class="text-center pa-3">
              <span class="text-subtitle-1">{{ gift.name }}</span>
            </v-card-title>

            <v-card-subtitle class="text-center pb-2">
              <v-chip size="x-small" variant="outlined" v-if="gift.provider">
                {{ gift.provider }}
              </v-chip>
            </v-card-subtitle>

            <v-card-text class="text-center pa-2">
              <div v-if="gift.productInfo?.price" class="text-h6 text-primary mb-1">
                {{ gift.productInfo.price }}
              </div>
              
              <div v-if="gift.reserved && gift.reservedBy" class="text-caption text-grey">
                Reservado por: {{ gift.reservedBy }}
              </div>
            </v-card-text>

            <v-card-actions class="flex-column pa-3 pt-0">
              <v-btn
                v-if="!gift.reserved && !isAdmin"
                color="primary"
                block
                size="small"
                @click="reserveGift(gift)"
              >
                <v-icon start size="small">mdi-cart-plus</v-icon>
                Reservar
              </v-btn>
              <v-btn
                :href="gift.url"
                target="_blank"
                variant="outlined"
                block
                size="small"
                :class="!gift.reserved && !isAdmin ? 'mt-2' : ''"
              >
                <v-icon start size="small">mdi-open-in-new</v-icon>
                Ver producto
              </v-btn>
              <v-btn
                v-if="isAdmin"
                color="error"
                variant="text"
                block
                size="small"
                class="mt-2"
                @click="deleteGift(gift.id)"
              >
                <v-icon start size="small">mdi-delete</v-icon>
                Eliminar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mensaje si no hay regalos -->
      <v-alert v-if="listData.gifts.length === 0" type="info" class="text-center my-4">
        <v-icon size="64" class="mb-4">mdi-clipboard-list-outline</v-icon>
        <div class="text-h6">No hay regalos en esta lista todavía</div>
        <div v-if="isAdmin" class="mt-2">¡Añade algunos para empezar!</div>
      </v-alert>
    </div>

    <!-- Dialog para añadir regalo (admin) -->
    <v-dialog v-model="showAddGiftDialog" max-width="600" :fullscreen="$vuetify.display.smAndDown">
      <v-card>
        <v-card-title class="pa-4">Añadir nuevo regalo</v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newGift.name"
            label="Nombre del regalo"
            prepend-inner-icon="mdi-rename-box"
            variant="outlined"
            class="mb-4"
          ></v-text-field>
          <v-text-field
            v-model="newGift.url"
            label="URL del producto"
            prepend-inner-icon="mdi-link"
            variant="outlined"
            @blur="extractProductInfo"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="showAddGiftDialog = false" variant="text">Cancelar</v-btn>
          <v-btn color="primary" @click="addGift" :loading="addingGift">Añadir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para reservar regalo -->
    <v-dialog v-model="showReserveDialog" max-width="500" :fullscreen="$vuetify.display.smAndDown">
      <v-card>
        <v-card-title class="pa-4">Reservar regalo</v-card-title>
        <v-card-text class="pa-4">
          <p class="mb-4">¿Quieres reservar "{{ selectedGift?.name }}"?</p>
          <v-text-field
            v-model="reserverName"
            label="Tu nombre (opcional)"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            hint="Para que el administrador sepa quién lo reservó"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="showReserveDialog = false" variant="text">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmReserve" :loading="reserving">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para editar lista -->
    <v-dialog v-model="showEditListDialog" max-width="600" :fullscreen="$vuetify.display.smAndDown">
      <v-card>
        <v-card-title class="pa-4">Editar lista</v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="editListForm.name"
            label="Nombre de la lista"
            prepend-inner-icon="mdi-text"
            variant="outlined"
            class="mb-4"
          ></v-text-field>
          
          <v-select
            v-model="editListForm.typeKey"
            :items="listTypeOptions"
            label="Tipo de lista"
            prepend-inner-icon="mdi-format-list-bulleted-type"
            variant="outlined"
            class="mb-4"
          ></v-select>

          <v-radio-group v-model="editListForm.isPrivate" label="Privacidad">
            <v-radio :value="false">
              <template v-slot:label>
                <div>Lista <strong class="text-success">pública</strong></div>
              </template>
            </v-radio>
            <v-radio :value="true">
              <template v-slot:label>
                <div>Lista <strong class="text-primary">privada</strong> (con contraseña)</div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-text-field
            v-if="editListForm.isPrivate"
            v-model="editListForm.password"
            label="Nueva contraseña (dejar vacío para mantener la actual)"
            prepend-inner-icon="mdi-lock"
            variant="outlined"
            type="password"
            class="mt-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="showEditListDialog = false" variant="text">Cancelar</v-btn>
          <v-btn color="primary" @click="saveListChanges" :loading="saving">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para eliminar lista -->
    <v-dialog v-model="showDeleteDialog" max-width="500" :fullscreen="$vuetify.display.smAndDown">
      <v-card>
        <v-card-title class="text-error pa-4">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Eliminar lista
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="mb-4">¿Estás seguro de que quieres eliminar esta lista?</p>
          <v-alert type="warning" variant="tonal" class="mb-4">
            Esta acción no se puede deshacer. Se eliminarán todos los regalos y la lista dejará de estar disponible.
          </v-alert>
          <v-text-field
            v-model="deleteConfirmation"
            label='Escribe "ELIMINAR" para confirmar'
            variant="outlined"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="showDeleteDialog = false" variant="text">Cancelar</v-btn>
          <v-btn 
            color="error" 
            @click="confirmDeleteList"
            :disabled="deleteConfirmation !== 'ELIMINAR'"
            :loading="deleting"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar = false"
          icon="mdi-close"
          size="small"
        ></v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/api';

export default {
  name: 'MyLists',
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    // Estados
    const listLoaded = ref(false);
    const loading = ref(false);
    const isAdmin = ref(false);
    const isAdminMode = ref(false);
    const requiresPassword = ref(false);
    const idFromUrl = ref(false);
    const addingGift = ref(false);
    const reserving = ref(false);
    const saving = ref(false);
    const deleting = ref(false);
    
    // Formulario
    const listId = ref('');
    const email = ref('');
    const password = ref('');
    
    // Lista
    const listData = ref({
      name: '',
      type: '',
      typeKey: '',
      icon: 'mdi-gift',
      color: 'primary',
      email: '',
      isPrivate: false,
      gifts: []
    });

    // UI
    const currentIcon = ref('mdi-gift');
    const imagen = ref('');
    const currentColor = ref('primary');
    const listTitle = ref('Accede a tu lista de regalos');
    const logoUrl = ref(require('@/assets/logo.png')); // Ruta a tu logo
    const showAddGiftDialog = ref(false);
    const showReserveDialog = ref(false);
    const showEditListDialog = ref(false);
    const showDeleteDialog = ref(false);
    const snackbar = ref(false);
    const snackbarText = ref('');
    const snackbarColor = ref('success');
    const errorMessage = ref('');
    
    // Regalos
    const newGift = ref({ url: '', name: '', productInfo: null });
    const selectedGift = ref(null);
    const reserverName = ref('');
    const deleteConfirmation = ref('');
    const editListForm = ref({
      name: '',
      typeKey: '',
      isPrivate: false,
      password: ''
    });

    // Tipos de lista
    const listTypes = {
      'nacimiento': { icon: 'mdi-baby-carriage', color: 'pink' },
      'cumpleanos': { icon: 'mdi-cake-variant', color: 'orange' },
      'boda': { icon: 'mdi-ring', color: 'purple' },
      'navidad': { icon: 'mdi-pine-tree', color: 'green' },
      'comunion': { icon: 'mdi-church', color: 'blue' },
      'milista': { icon: 'mdi-emoticon-happy', color: 'red' }
    };

    const listTypeOptions = [
      { title: 'Nacimiento / Baby shower', value: 'nacimiento' },
      { title: 'Cumpleaños', value: 'cumpleanos' },
      { title: 'Boda', value: 'boda' },
      { title: 'Navidad / Amigo invisible', value: 'navidad' },
      { title: 'Comunión', value: 'comunion' },
      { title: 'Mi lista', value: 'milista' }
    ];

    const reservedCount = computed(() => {
      return listData.value.gifts.filter(g => g.reserved).length;
    });

    const availableCount = computed(() => {
      return listData.value.gifts.filter(g => !g.reserved).length;
    });

    onMounted(() => {
      // Obtener ID desde la URL si existe (ahora es /:id en vez de /lista/:id)
      const urlId = route.params.id;
      
      if (urlId) {
        listId.value = urlId;
        idFromUrl.value = true;
        
        // Verificar si hay datos de admin en sessionStorage
        const adminData = sessionStorage.getItem('adminAccess');
        if (adminData) {
          try {
            const parsed = JSON.parse(adminData);
            // Verificar que el listId coincida
            if (parsed.listId === urlId && parsed.isAdmin && parsed.email) {
              isAdminMode.value = true;
              email.value = parsed.email;
              
              // Limpiar sessionStorage después de usarlo
              sessionStorage.removeItem('adminAccess');
            }
          } catch (error) {
            console.error('Error parseando adminAccess:', error);
            sessionStorage.removeItem('adminAccess');
          }
        }
        
        // Cargar automáticamente si viene desde URL
        loadList();
      }
    });

    const toggleAdminMode = () => {
      const previousMode = isAdminMode.value;
      isAdminMode.value = !isAdminMode.value;
      email.value = '';
      password.value = '';
      errorMessage.value = '';
      
      // Si cambiamos de admin a visitante y había un error de password required,
      // mantener requiresPassword en true para mostrar el formulario
      if (previousMode && !isAdminMode.value) {
        // De admin a visitante
        // No cambiar requiresPassword si ya está en true
      } else {
        // De visitante a admin
        requiresPassword.value = false;
      }
      
      // Si estamos en la pantalla de error, intentar recargar
      if (!listLoaded.value && !loading.value) {
        // Pequeño delay para que Vue actualice el DOM
        setTimeout(() => {
          loadList();
        }, 100);
      }
    };

    const loadList = async () => {
      if (!listId.value) {
        showSnackbar('Por favor introduce el código de la lista', 'error');
        return;
      }

      loading.value = true;
      
      try {
        const response = await ApiService.accessList(
          listId.value,
          password.value || null,
          email.value || null,
          isAdminMode.value
        );

        // Cargar datos
        listData.value = {
          ...response,
          icon: listTypes[response.typeKey]?.icon || 'mdi-gift',
          color: listTypes[response.typeKey]?.color || 'primary'
        };

        // IMPORTANTE: Asignar correctamente el estado de administrador
        isAdmin.value = response.isAdmin === true;
        // Actualizar icono actual
        currentIcon.value = listData.value.icon;
        currentColor.value = listData.value.color;

        listLoaded.value = true;
        requiresPassword.value = false;

        // Actualizar URL si no venía desde la URL
        if (!idFromUrl.value) {
          router.push({ name: 'viewlist', params: { id: listId.value } });
          idFromUrl.value = true; // Marcar que ahora sí está en la URL
        }
        
        showSnackbar(
          isAdmin.value ? '✅ Acceso como administrador' : 'Lista cargada correctamente',
          'success'
        );

      } catch (err) {
        console.error('❌ Error cargando lista:', err);

        if (err.message.includes('Password required')) {
          requiresPassword.value = true;
          errorMessage.value = 'Esta lista requiere contraseña';
          showSnackbar('Esta lista requiere contraseña', 'warning');
          // No mostrar como error general, sino mantener el formulario visible
          listLoaded.value = false;
        } else if (err.message.includes('Invalid password')) {
          requiresPassword.value = true; // Mantener el formulario de contraseña visible
          errorMessage.value = 'Contraseña incorrecta';
          showSnackbar('Contraseña incorrecta', 'error');
          listLoaded.value = false;
        } else if (err.message.includes('not found')) {
          errorMessage.value = 'Lista no encontrada. Verifica que el código sea correcto.';
          showSnackbar('Lista no encontrada', 'error');
          listLoaded.value = false;
        } else if (err.message.includes('Unauthorized')) {
          errorMessage.value = 'Email no autorizado para esta lista';
          showSnackbar('Email no autorizado', 'error');
          listLoaded.value = false;
        } else {
          errorMessage.value = err.message || 'Error al cargar la lista';
          showSnackbar(err.message || 'Error al cargar la lista', 'error');
          listLoaded.value = false;
        }
      } finally {
        loading.value = false;
      }
    };

    const extractProductInfo = () => {
      if (!newGift.value.url) return;

      try {
        const url = new URL(newGift.value.url);
        const hostname = url.hostname.replace('www.', '').toLowerCase();

        let provider = hostname;
        let imageUrl = null;

        if (hostname.includes('amazon')) {
          provider = 'Amazon';
          // Extraer ASIN de Amazon
          const asinMatch = newGift.value.url.match(/\/dp\/([A-Z0-9]{10})/);
          if (asinMatch) {
            const asin = asinMatch[1];
            // Usar la URL estándar de imágenes de Amazon
            imageUrl = `https://images-na.ssl-images-amazon.com/images/P/${asin}.jpg`;
          }
        } 
        else if (hostname.includes('ebay')) {
          provider = 'eBay';
        } 
        else if (hostname.includes('booking')) {
          provider = 'Booking';
        } 
        else if (hostname.includes('smartbox')) {
          provider = 'Smartbox';
        } 
        else if (hostname.includes('etsy')) {
          provider = 'Etsy';
        } 
        else if (hostname.includes('aliexpress')) {
          provider = 'AliExpress';
        }
        else if (hostname.includes('elcorteingles')) {
          provider = 'El Corte Inglés';
        }
        else if (hostname.includes('fnac')) {
          provider = 'FNAC';
        }
        else if (hostname.includes('mediamarkt')) {
          provider = 'MediaMarkt';
        }
        else if (hostname.includes('zara')) {
          provider = 'Zara';
        }

        newGift.value.productInfo = { provider, image: imageUrl };

        // Si no pudimos obtener la imagen directamente, intentar obtenerla del servidor
        if (!imageUrl) {
          fetchProductImage(newGift.value.url);
        }

      } catch (error) {
        console.error('Error extrayendo info del producto:', error);
      }
    };

    const fetchProductImage = async (productUrl) => {
      try {
        // Llamar a un endpoint del backend que haga scraping
        const response = await fetch(`${process.env.VUE_APP_API_URL || 'http://localhost:3000/api'}/scrape-product`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: productUrl })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.image) {
            newGift.value.productInfo = {
              ...newGift.value.productInfo,
              image: data.image,
              title: data.title || newGift.value.name,
              price: data.price || null
            };
          }
        }
      } catch (error) {
        console.error('❌ Error obteniendo imagen del producto:', error);
        // No hacer nada, simplemente no habrá imagen
      }
    };

    const addGift = async () => {
      if (!newGift.value.name || !newGift.value.url) {
        showSnackbar('Completa el nombre y la URL del regalo', 'error');
        return;
      }

      addingGift.value = true;

      try {
        const giftData = {
          name: newGift.value.name,
          url: newGift.value.url,
          provider: newGift.value.productInfo?.provider || null,
          productInfo: newGift.value.productInfo || {}
        };

        await ApiService.addGift(listId.value, listData.value.email, giftData);

        // Recargar lista para obtener el nuevo regalo con su ID
        const tempIdFromUrl = idFromUrl.value;
        idFromUrl.value = true; // Evitar que se actualice la URL
        await loadList();
        idFromUrl.value = tempIdFromUrl;

        showAddGiftDialog.value = false;
        newGift.value = { url: '', name: '', productInfo: null };
        showSnackbar('Regalo añadido correctamente', 'success');

      } catch (error) {
        console.error('Error añadiendo regalo:', error);
        showSnackbar(error.message || 'Error al añadir el regalo', 'error');
      } finally {
        addingGift.value = false;
      }
    };

    const deleteGift = async (giftId) => {
      if (!confirm('¿Estás seguro de que quieres eliminar este regalo?')) {
        return;
      }

      try {
        await ApiService.deleteGift(listId.value, giftId, listData.value.email);

        // Eliminar localmente
        listData.value.gifts = listData.value.gifts.filter(g => g.id !== giftId);

        showSnackbar('Regalo eliminado', 'success');

      } catch (error) {
        console.error('Error eliminando regalo:', error);
        showSnackbar(error.message || 'Error al eliminar el regalo', 'error');
      }
    };

    const reserveGift = (gift) => {
      selectedGift.value = gift;
      showReserveDialog.value = true;
    };

    const confirmReserve = async () => {
      if (!selectedGift.value) return;

      reserving.value = true;

      try {
        await ApiService.reserveGift(
          selectedGift.value.id,
          reserverName.value || 'Anónimo'
        );

        // Actualizar localmente
        const giftIndex = listData.value.gifts.findIndex(g => g.id === selectedGift.value.id);
        if (giftIndex !== -1) {
          listData.value.gifts[giftIndex].reserved = true;
          listData.value.gifts[giftIndex].reservedBy = reserverName.value || 'Anónimo';
        }

        showSnackbar('¡Regalo reservado! Gracias 🎁', 'success');
        showReserveDialog.value = false;
        reserverName.value = '';
        selectedGift.value = null;

      } catch (error) {
        console.error('Error reservando regalo:', error);
        showSnackbar(error.message || 'Error al reservar el regalo', 'error');
      } finally {
        reserving.value = false;
      }
    };

    const shareList = () => {
      const url = `${window.location.origin}/${listId.value}`;
      navigator.clipboard.writeText(url);
      showSnackbar('¡Enlace copiado al portapapeles!', 'success');
    };

    const openEditDialog = () => {
      editListForm.value = {
        name: listData.value.name,
        typeKey: listData.value.typeKey,
        isPrivate: listData.value.isPrivate,
        password: ''
      };
      showEditListDialog.value = true;
    };

    const saveListChanges = async () => {
      saving.value = true;

      try {
        const updateData = {
          name: editListForm.value.name,
          typeKey: editListForm.value.typeKey,
          isPrivate: editListForm.value.isPrivate
        };

        if (editListForm.value.password) {
          updateData.password = editListForm.value.password;
        }

        await ApiService.updateList(listId.value, listData.value.email, updateData);

        // Actualizar datos locales
        listData.value.name = editListForm.value.name;
        listData.value.typeKey = editListForm.value.typeKey;
        listData.value.isPrivate = editListForm.value.isPrivate;

        // Actualizar icono y color
        const typeInfo = listTypes[editListForm.value.typeKey];
        if (typeInfo) {
          listData.value.icon = typeInfo.icon;
          listData.value.color = typeInfo.color;
          currentIcon.value = typeInfo.icon;
          currentColor.value = typeInfo.color;

          const typeOption = listTypeOptions.find(opt => opt.value === editListForm.value.typeKey);
          if (typeOption) {
            listData.value.type = typeOption.title;
          }
        }

        showEditListDialog.value = false;
        showSnackbar('Lista actualizada correctamente', 'success');

      } catch (error) {
        console.error('Error actualizando lista:', error);
        showSnackbar(error.message || 'Error al actualizar la lista', 'error');
      } finally {
        saving.value = false;
      }
    };

    const confirmDeleteList = async () => {
      if (deleteConfirmation.value !== 'ELIMINAR') return;

      deleting.value = true;

      try {
        await ApiService.deleteList(listId.value, listData.value.email);

        showSnackbar('Lista eliminada correctamente', 'error');

        // Volver al inicio
        setTimeout(() => {
          router.push('/');
        }, 1500);

      } catch (error) {
        console.error('Error eliminando lista:', error);
        showSnackbar(error.message || 'Error al eliminar la lista', 'error');
      } finally {
        deleting.value = false;
      }
    };

    const showSnackbar = (text, color = 'success') => {
      snackbarText.value = text;
      snackbarColor.value = color;
      snackbar.value = true;
    };

    const onImageError = (gift) => {
      // Eliminar la imagen para que muestre el fallback
      if (gift.productInfo) {
        gift.productInfo.image = null;
      }
    };

    return {
      listLoaded,
      loading,
      isAdmin,
      isAdminMode,
      requiresPassword,
      idFromUrl,
      addingGift,
      reserving,
      saving,
      deleting,
      listId,
      email,
      password,
      listData,
      currentIcon,
      imagen,
      currentColor,
      listTitle,
      logoUrl,
      showAddGiftDialog,
      showReserveDialog,
      showEditListDialog,
      showDeleteDialog,
      snackbar,
      snackbarText,
      snackbarColor,
      errorMessage,
      newGift,
      selectedGift,
      reserverName,
      deleteConfirmation,
      editListForm,
      listTypeOptions,
      reservedCount,
      availableCount,
      toggleAdminMode,
      loadList,
      extractProductInfo,
      addGift,
      deleteGift,
      reserveGift,
      confirmReserve,
      shareList,
      openEditDialog,
      saveListChanges,
      confirmDeleteList,
      onImageError
    };
  }
};
</script>

<style scoped>
.center-container {
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gift-card {
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
}

.gift-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.gift-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  background: #f5f5f5;
}

.default-gift-image {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
}

.reserved-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.gift-logo {
  opacity: 0.7;
  filter: grayscale(20%);
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .admin-actions {
    flex-direction: column;
  }

  .admin-actions .v-btn {
    width: 100%;
  }

  .gift-icon-container {
    height: 150px;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .gift-icon-container {
    height: 160px;
  }
}
</style>